import { useState, useEffect, useRef } from 'react';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import IconUp from '../icons/up';
import IconDown from '../icons/down';
import IconClose from '../icons/close';
import OptionList from './option-list';
import usePrevious from '../../hooks/use-previous';
import { ApplicationSelectorTypes } from './types';

import styles from './application-selector.module.css';

export default function ApplicationSelector({
  title,
  placeholder,
  onButtonClick,
  listPosition,
  list,
  isLoading,
  onSearch,
  onApplicationClick,
  clearState,
  tabIndex,
  fullWidth,
  withoutButton,
  withClearButton,
  searchQuery,
  error,
}: ApplicationSelectorTypes) {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const [listVisible, setListVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedNode, setSelectedNode] = useState(1);

  const prevOpen = usePrevious(listVisible);

  useEffect(() => {
    if (clearState) {
      setSearchValue('');
      onSearch('');
    }
  }, [clearState, onSearch]);

  useEffect(() => {
    if (searchQuery) {
      setSearchValue(searchQuery);
    }
  }, [searchQuery]);

  const onScroll = (e: any) => {
    const nodes = ulRef?.current?.querySelectorAll('li');

    if (nodes) {
      const firstNode = nodes[1];
      const elHeight = firstNode.clientHeight;

      const node = nodes[Math.round(e.target.scrollTop / elHeight)];

      if (node) {
        // @ts-ignore
        const currentIndex = [].indexOf.call(nodes, node);

        if (currentIndex && currentIndex !== undefined && currentIndex !== -1) {
          const prevNode = nodes[selectedNode];
          const currentNode = nodes[currentIndex];

          prevNode?.classList?.remove(styles.hovered);
          currentNode?.classList.add(styles.hovered);

          setSelectedNode(currentIndex);
        }
      }
    }
  };

  const onSelectNode = ({
    node,
    selected,
  }: {
    node: HTMLLIElement;
    selected: number;
  }) => {
    if (node) {
      node.classList.add(styles.hovered);

      const elHeight = node.clientHeight;
      const scrollTop = ulRef?.current?.scrollTop;

      if (scrollTop !== undefined && ulRef?.current) {
        const ulHeight = ulRef?.current?.clientHeight;
        const viewport = scrollTop - 60 + ulHeight;
        const elOffset = elHeight * selected;

        if (elOffset < scrollTop || elOffset + elHeight > viewport) {
          if (selectedNode !== 1) {
            ulRef?.current.scrollTo(0, elOffset + 20);
          } else {
            ulRef?.current.scrollTo(0, 0);
          }
        }
      }
    }
  };

  const onEnter = () => {
    list?.forEach((item, index) => {
      if (index === selectedNode) {
        setSearchValue(item.name);
        onApplicationClick(item.id);
      }
    });
  };

  const onUlKeydown = (e: { code: string; preventDefault: () => void }) => {
    const nodes = ulRef?.current?.querySelectorAll('li');
    let nextNode;

    if (nodes) {
      if (e.code === 'ArrowDown' && nodes[selectedNode + 1]) {
        setSelectedNode(selectedNode + 1);
        nextNode = selectedNode + 1;
        onSelectNode({
          node: nodes[selectedNode + 1],
          selected: selectedNode + 1,
        });
      }

      if (e.code === 'ArrowUp') {
        if (selectedNode !== 1) {
          nextNode = selectedNode - 1;

          setSelectedNode(selectedNode - 1);
          onSelectNode({
            node: nodes[selectedNode - 1],
            selected: selectedNode - 1,
          });
        } else {
          setSelectedNode(1);
          ulRef?.current?.scrollTo(0, 0);
        }
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        const prevLi = nodes[selectedNode];

        if (prevLi && nodes[nextNode as number]) {
          prevLi.classList.remove(styles.hovered);
        }
      }

      e.preventDefault();
    }
  };

  useEffect(() => {
    if (ulRef && ulRef.current && listVisible) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes[1]) {
        nodes[1].classList.add(styles.hovered);
      }
    }
  }, [ulRef, listVisible]);

  useEffect(() => {
    if (!listVisible && prevOpen) {
      setSelectedNode(1);

      ulRef?.current?.removeEventListener('keydown', () => {});
    }
  }, [ulRef, listVisible, prevOpen]);

  return (
    <ClickOutsideDetector
      setOptionsListVisible={setListVisible}
      className={`${styles.applicationSelector} ${
        fullWidth ? styles?.fullWidth : ''
      }`}
    >
      <>
        {title && (
          <div className={styles.applicationSelectorTitle}>{title}</div>
        )}
        <div
          className={`${styles.applicationSelectorWrapper} ${
            fullWidth ? styles?.fullWidth : ''
          }`}
        >
          <div className={styles.applicationSelectorInputWrapper}>
            <input
              type="text"
              value={searchValue ?? ''}
              onChange={(e) => {
                setSearchValue(e.target.value);
                onSearch(e.target.value);
                setSelectedNode(1);
              }}
              tabIndex={tabIndex}
              className={`${styles.applicationSearchInput}  ${
                error ? styles.selectInputSearchError : ''
              }`}
              placeholder={placeholder}
              onFocus={() => setListVisible(true)}
              onKeyDown={(e) => {
                if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                  onUlKeydown(e);
                }

                if (e.code === 'Enter') {
                  onEnter();
                }

                if (e.code === 'Tab') {
                  setListVisible(false);
                }
              }}
              autoComplete="off"
            />
            {listVisible ? (
              <IconUp className={styles.iconArrow} />
            ) : (
              <IconDown className={styles.iconArrow} />
            )}
            {searchValue && withClearButton && (
              <button
                type="button"
                className={`${styles.iconWrapper} ${styles.iconWrapperClear}`}
                onClick={() => {
                  setSearchValue('');
                  onSearch('');
                  onApplicationClick('');
                }}
                tabIndex={-1}
                aria-label="Close"
              >
                <IconClose className={styles.icon} />
              </button>
            )}
          </div>
          {listVisible && (
            <OptionList
              onButtonClick={onButtonClick}
              listPosition={listPosition}
              setListVisible={setListVisible}
              setSearchValue={setSearchValue}
              list={list}
              isLoading={isLoading}
              onApplicationClick={onApplicationClick}
              onScroll={onScroll}
              ulRef={ulRef}
              withoutButton={withoutButton}
            />
          )}
        </div>
        {error && <div className={styles.autocompleteError}>{error}</div>}
      </>
    </ClickOutsideDetector>
  );
}
